import { useEffect, useState } from 'react';
import { Button, Col, Form, FormFeedback, FormGroup, Input, Label, Row } from 'reactstrap';
import Reaptcha from 'reaptcha';
import { useAPIErrorContext } from './../../contexts/apiErrorContext';
import { useLocation } from 'react-router-dom';
import { useFetchWrapper } from '../../hooks/useFetchWrapper';
import Helper from '../../utilities/helper';
import { useQuery } from 'graphql-hooks';
import { ErrorBanner } from '../Shared/ErrorBanner';
import { clientConfig } from '../../utilities/configService';
import { FormContainer } from '../Shared/Forms/FormContainer';
import { ContentBlock } from '../Shared/Content/ContentBlock';
import { ContentBody } from '../Shared/Content/ContentBody';

interface IProps {
    closeHandler: () => void
}

interface IOrganizationType {
    organizationTypeID: string,
    organizationTypeName: string
}

const CMS_QUERY = `query datoQuery {
    pageData: pageContactConfirmation {
        fieldBodyContent1
    }
}`;

export const MiniContact = ({ closeHandler }: IProps) => {
    const fetchWrapper = useFetchWrapper();
    const { addError } = useAPIErrorContext();
    const { loading, error, data } = useQuery(CMS_QUERY);
    const [captchaVerified, setCaptchaVerified] = useState(false);
    const [formSubmitted, setFormSubmitted] = useState(false);
    const [organizationTypeOptions, setOrganizationTypeOptions] = useState([]);
    const [organizationTypeSelect, setOrganizationTypeSelect] = useState("0");
    const [formErrors, setFormErrors] = useState({
        firstName: '',
        lastName: '',
        email: '',
        organizationType: '',
        organizationName: ''
    });

    const [contactForm, setContactForm] = useState({
        firstName: '',
        lastName: '',
        email: '',
        organizationName: '',
        message: ''
    });

    const location = useLocation();
    useEffect(() => {
        Helper.setPagePath(location.pathname);
    }, [location]);

    // eslint-disable-next-line
    useEffect(() => {
        const populateOrganizationType = () => {
            fetchWrapper.get('organization/gettypes')
                .then((data) => {
                    if (data.success && data.organizationTypes) {
                        setOrganizationTypeOptions(data.organizationTypes);
                    }
                });
        };
        populateOrganizationType();
    }, []);



    function handleFirstNameChange(e: { target: { value: any; }; }) {
        const updatedForm = {
            ...contactForm,
            firstName: e.target.value
        };
        setContactForm(updatedForm);
        if (formErrors.firstName !== '') {
            validateForm(updatedForm);
        }
    }

    function handleLastNameChange(e: { target: { value: any; }; }) {
        const updatedForm = {
            ...contactForm,
            lastName: e.target.value
        };
        setContactForm(updatedForm);
        if (formErrors.lastName !== '') {
            validateForm(updatedForm);
        }
    }

    function handleEmailChange(e: { target: { value: any; }; }) {
        const updatedForm = {
            ...contactForm,
            email: e.target.value
        };
        setContactForm(updatedForm);
        if (formErrors.email !== '') {
            validateForm(updatedForm);
        }
    }

    function handleOrganizationNameChange(e: { target: { value: any; }; }) {
        const updatedForm = {
            ...contactForm,
            organizationName: e.target.value
        };
        setContactForm(updatedForm);
        if (formErrors.organizationName !== '') {
            validateForm(updatedForm);
        }
    }

    function handleMessageChange(e: { target: { value: any; }; }) {
        setContactForm({
            ...contactForm,
            message: e.target.value
        });
    }

    const handleOrganizationTypeChange = (e: { target: { value: any; }; }) => {
        setOrganizationTypeSelect(e.target.value);
        if (formErrors.organizationType !== '') {
            validateForm(contactForm, e.target.value);
        }
    };

    const emailValidationPattern = new RegExp(/^[a-zA-Z0-9.!#$%&'*+/=?^_{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)+$/);
    const containsOnlySpacesValidationPattern = "^\\s+$";

    //Validate Form
    const errorClass = (error: string | any[]) => {
        return (error.length === 0 ? '' : 'is-invalid');
    }

    // Change handlers pass in the values they just set, since state updates are not visible until the next render.
    const validateForm = (form = contactForm, organizationType = organizationTypeSelect) => {
        var fieldValidationError = '';
        var fieldErrors = {
            firstName: '',
            lastName: '',
            email: '',
            organizationType: '',
            organizationName: ''
        }

        if (form.firstName == null ||
            form.firstName.length <= 0 ||
            form.firstName.match(containsOnlySpacesValidationPattern)) {
            fieldValidationError = 'First Name is required.';
            fieldErrors.firstName = 'First Name is required.'
        }

        if (form.lastName == null ||
            form.lastName.length <= 0 ||
            form.lastName.match(containsOnlySpacesValidationPattern)) {
            fieldValidationError = 'Last Name is required.';
            fieldErrors.lastName = 'Last Name is required.'
        }

        if (form.organizationName == null ||
            form.organizationName.length <= 0 ||
            form.organizationName.match(containsOnlySpacesValidationPattern)) {
            fieldValidationError = 'Organization Name is required.';
            fieldErrors.organizationName = 'Organization Name is required.'
        }

        if (form.email.length <= 0) {
            fieldValidationError = 'Email is required.';
            fieldErrors.email = 'Email is required.'
        }
        else if (!form.email.match(emailValidationPattern)) {
            fieldValidationError = 'Email is invalid.';
            fieldErrors.email = 'Email is invalid.'
        }

        if (organizationType === '0') {
            fieldValidationError = 'Orgainization Type is required.';
            fieldErrors.organizationType = 'Organization Type is required.'
        }

        // Always show the latest result, so fixing the last remaining error clears it too.
        setFormErrors(fieldErrors);

        if (fieldValidationError.length > 0) {
            console.log("formErrors state: ", formErrors);
            return false;
        }
        else {
            return true;
        }
    }

    const onVerify = async (recaptchaResponse: any) => {
        if (recaptchaResponse) {
            var recaptchaverificationrequest = { token: recaptchaResponse };

            fetchWrapper.post('recaptchaverification', 'application/json', recaptchaverificationrequest)
                .then((data) => {
                    if (data.success) {
                        setCaptchaVerified(true);
                    }
                });
        }
    };

    const validateAndPostForm = async (e: any) => {

        e.preventDefault();

        let formIsValid = validateForm();
        const applicationName = clientConfig?.APPLICATION_NAME || '';

        if (formIsValid) {
            var contactUsRequest = {
                firstName: contactForm.firstName,
                lastName: contactForm.lastName,
                organizationName: contactForm.organizationName,
                email: contactForm.email,
                message: contactForm.message,
                organizationRole: '',
                organizationTypeID: organizationTypeSelect,
                applicationName: applicationName
            };

            fetchWrapper.post('contact', 'application/json', contactUsRequest)
                .then((data) => {
                    if (data.success) {
                        setFormSubmitted(true);
                    }
                    else {
                        setFormSubmitted(false);
                        addError("Error submitting Mini Contact Us form", data.message);
                    }
                }).catch((error: any) => {
                    setFormSubmitted(false);
                    addError("Error submitting Mini Contact Us form", error);
                });
        }
    };

    const cancelForm = () => {
        setContactForm({ firstName: '', lastName: '', organizationName: '', email: '', message: '' });
        setOrganizationTypeSelect('');
        setFormErrors({ firstName: '', lastName: '', organizationName: '', organizationType: '', email: '' });
        closeHandler();
    }

    return (
        <div className="contactUsContainer miniContactUs">
            {formSubmitted ? (
                <div className="contactRequest">
                    <ErrorBanner key="contactUsPage" errorCode="form" />
                    <ContentBlock className="contactUsConfirmation">
                        <ContentBody>
                            <div className="text-align-center">
                                {!error && !loading && data.pageData.fieldBodyContent1 ? (
                                    <div className="body">
                                        <p dangerouslySetInnerHTML={{ __html: data.pageData.fieldBodyContent1 }} />
                                    </div>
                                ) : (
                                    <div className="body">
                                        <p>Thank you for contacting the Fleet Advisory Services Team. One of our advisors will reach out to you within 2 business days. We look forward to helping you meet your electrification goals!</p>
                                    </div>
                                )}
                                <br/><br/>
                                <Button className="cancelButton hollowPos primaryButton iconCloseX iconBlack" onClick={cancelForm}>CLOSE</Button>
                            </div>
                        </ContentBody>
                    </ContentBlock>

                </div>
            ) : (
                <div className="contactRequest">
                    <ErrorBanner key="contactUsPage" errorCode="form" />
                    <FormContainer title="" description="">
                        <Row className="contactRequestForm formBody">
                            <Col>
                                <Form>
                                    <Row>
                                        <Col lg={2} className="formRequiredContainer">
                                            <span className="formRequired"></span> = required field
                                        </Col>
                                        <Col lg={3} className="formGroupContainer">
                                            <FormGroup row className={`form-group ${errorClass(formErrors.firstName)}`}>
                                                <Label for="firstName" className="formRequired ">First Name</Label>
                                                <Col>
                                                    <Input className="fieldWidth215" type="text" name="firstname" id="firstName" placeholder="" required maxLength={150} value={contactForm.firstName} onChange={handleFirstNameChange} invalid={formErrors.firstName !== ''} pattern={containsOnlySpacesValidationPattern.toString()} />
                                                    <FormFeedback>{formErrors.firstName}</FormFeedback>
                                                </Col>
                                            </FormGroup>
                                            <FormGroup row className={`form-group ${errorClass(formErrors.lastName)}`}>
                                                <Label for="lastName" className="formRequired">Last Name</Label>
                                                <Col>
                                                    <Input className="fieldWidth215" type="text" name="lastname" id="lastName" placeholder="" required maxLength={150} value={contactForm.lastName} onChange={handleLastNameChange} invalid={formErrors.lastName !== ''} pattern={containsOnlySpacesValidationPattern.toString()} />
                                                    <FormFeedback>{formErrors.lastName}</FormFeedback>
                                                </Col>
                                            </FormGroup>
                                            <FormGroup row className={`form-group ${errorClass(formErrors.email)}`}>
                                                <Label for="emailAddress" className="formRequired">Email Address</Label>
                                                <Col>
                                                    <Input className="fieldWidth215" type="email" name="email" id="emailAddress" placeholder="" required value={contactForm.email} onChange={handleEmailChange} invalid={formErrors.email !== ''}
                                                        pattern={emailValidationPattern.toString()} />
                                                    <FormFeedback>{formErrors.email}</FormFeedback>
                                                </Col>
                                            </FormGroup>
                                        </Col>
                                        <Col lg={3} className="formGroupContainer">
                                            <FormGroup row >
                                                <Label for="organizationName" className="formRequired">Organization Name</Label>
                                                <Col>
                                                    <Input className="fieldWidth215" type="text" name="organizationName" id="organizationName" placeholder="" maxLength={150} value={contactForm.organizationName} onChange={handleOrganizationNameChange} invalid={formErrors.organizationName !== ''} />
                                                    <FormFeedback>{formErrors.organizationName}</FormFeedback>
                                                </Col>
                                            </FormGroup>
                                            <FormGroup row className={`form-group ${errorClass(formErrors.organizationType)}`}>
                                                <Label for="organizationType" className="formRequired">Organization Type</Label>
                                                <Col>
                                                    <Input className="fieldWidth215" type="select" name="select" id="organizationType" value={organizationTypeSelect} required onChange={handleOrganizationTypeChange} invalid={formErrors.organizationType !== ''}>
                                                        <option key="0" value="0" disabled>
                                                            Select an option
                                                        </option>
                                                        {organizationTypeOptions.map((option: IOrganizationType) => {
                                                            return (
                                                                <option key={option.organizationTypeID} value={option.organizationTypeID}>
                                                                    {option.organizationTypeName}
                                                                </option>)
                                                        })};
                                                    </Input>
                                                    <FormFeedback>{formErrors.organizationType}</FormFeedback>
                                                </Col>
                                            </FormGroup>
                                        </Col>
                                        <Col lg={4} className="formGroupContainer">
                                            <FormGroup row>
                                                <Label for="message" className="notRequired">Message</Label>
                                                <Col>
                                                    <Input className="fieldWidth300" type="textarea" name="text" id="message" maxLength={2000} value={contactForm.message} onChange={handleMessageChange} />
                                                </Col>
                                            </FormGroup>
                                            <FormGroup row>
                                                <Col>
                                                    <div className="recaptchaSection">
                                                        <Reaptcha sitekey={import.meta.env.REACT_APP_CAPTCHA_SITE_KEY ? import.meta.env.REACT_APP_CAPTCHA_SITE_KEY : ''} onVerify={onVerify} />
                                                    </div>
                                                </Col>
                                            </FormGroup>
                                        </Col>
                                    </Row>

                                </Form>
                            </Col>
                        </Row>
                        <Row className="actionButtonRow">
                            <Col sm={12} className="actionButtonColumn">
                                <Button className="submitButton solidNeg primaryButton iconRightArrow" onClick={validateAndPostForm} disabled={!captchaVerified}>Submit</Button>
                                <Button className="cancelButton hollowPos primaryButton iconCloseX iconBlack text-align-right" onClick={cancelForm}>CANCEL</Button>
                            </Col>

                        </Row>
                    </FormContainer>
                </div>
            )}
        </div>
    )
};
